import {
  startOfMonth,
  startOfYear,
  subMonths,
  differenceInMonths,
  addMonths,
  max,
  min,
  differenceInWeeks,
  endOfMonth,
  addWeeks,
  startOfWeek,
  isSameMonth,
  isSameWeek,
  format,
  setYear,
  endOfWeek,
} from 'date-fns'

import { ControleQualiteIntervalleHistorique } from '@api/enum'

const NOW = new Date()
const LATEST = setYear(NOW, 2015)

const MODES = {
  all: {
    dateDebut: LATEST,
    dateFin: endOfMonth(NOW),
    intervalle: ControleQualiteIntervalleHistorique.MOIS,
  },
  year: {
    dateDebut: startOfYear(NOW),
    dateFin: endOfMonth(NOW),
    intervalle: ControleQualiteIntervalleHistorique.MOIS,
  },
  halfYear: {
    dateDebut: subMonths(NOW, 6),
    dateFin: endOfMonth(NOW),
    intervalle: ControleQualiteIntervalleHistorique.MOIS,
  },
  month: {
    dateDebut: startOfMonth(NOW),
    dateFin: endOfMonth(NOW),
    intervalle: ControleQualiteIntervalleHistorique.SEMAINE,
  },
} as const

const getAxis = (mode: keyof typeof MODES, datas: ControleQualiteHistoriqueResponse[]) => {
  switch (mode) {
    case 'all':
      if (datas.length === 0) {
        return []
      }

      const mini = startOfMonth(min(datas.map((d) => new Date(d.date))))
      const maxi = startOfMonth(max(datas.map((d) => new Date(d.date))))
      return Array.from(new Array(differenceInMonths(maxi, mini) + 1)).map((_, idx) => addMonths(mini, idx))
    case 'halfYear':
      const half = subMonths(NOW, 6)
      return Array.from(new Array(6)).map((_, idx) => addMonths(half, idx + 1))
    case 'month':
      const month = startOfWeek(startOfMonth(NOW), { weekStartsOn: 1 })
      return Array.from(new Array(differenceInWeeks(endOfMonth(NOW), month) + 1))
        .map((_, idx) => addWeeks(month, idx))
        .filter((date) => isSameMonth(date, NOW))
    case 'year':
      const year = startOfYear(NOW)
      return Array.from(new Array(differenceInMonths(NOW, year) + 1)).map((_, idx) => addMonths(year, idx))
    default:
      return []
  }
}

const findAxisIndex = (mode: keyof typeof MODES, date: Date, axis: Date[]) => {
  if (mode === 'month') {
    return axis.findIndex((a) => isSameWeek(date, a))
  } else {
    return axis.findIndex((a) => isSameMonth(date, a))
  }
}

const findAxisLabels = (mode: keyof typeof MODES, axis: Date[], i18n: I18nState) => {
  if (mode === 'month') {
    return axis.map((start) => {
      const end = endOfWeek(start, { weekStartsOn: 1 })
      return i18n.t('screens.controleDetail.graphWeek', { from: start, to: end })
    })
  } else if (mode === 'all') {
    return axis.map((a) => format(a, 'MMM yyyy', { locale: i18n.locales[i18n.lang] }))
  } else {
    return axis.map((a) => format(a, 'MMM', { locale: i18n.locales[i18n.lang] }))
  }
}

export { MODES, getAxis, findAxisIndex, findAxisLabels }
